/* ============================================================
   Sticky Add to Cart Bar — Custom Element
   Mobile only · activates Shopify's existing product form
   Draft theme 188261794111
   ============================================================ */

if (!customElements.get('sticky-atc-bar')) {
  class StickyAtcBar extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      this._connected = true;
      this._sectionId = this.dataset.sectionId;
      this._pastOriginalButton = false;
      this._isDrawerOpen = false;
      this._resizeTimer = null;

      this._stickyBtn = this.querySelector('.sticky-atc-bar__btn');
      this._priceEl = this.querySelector('.sticky-atc-bar__price');
      this._liveEl = this.querySelector('.sticky-atc-bar__live');

      if (!this._sectionId || !this._stickyBtn) return;

      this._onStickyClick = this._onStickyClick.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onIntersect = this._onIntersect.bind(this);
      this._onProductMutate = this._onProductMutate.bind(this);
      this._onDrawerMutate = this._onDrawerMutate.bind(this);

      this._stickyBtn.addEventListener('click', this._onStickyClick);
      window.addEventListener('resize', this._onResize);

      this._attachProductObserver();
      this._attachIntersectionObserver();
      this._attachDrawerObserver();
      this._mirrorState();
      this._updateDrawerState();
      this._updateVisibility();
    }

    disconnectedCallback() {
      this._connected = false;

      if (this._stickyBtn && this._onStickyClick) {
        this._stickyBtn.removeEventListener('click', this._onStickyClick);
      }

      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }

      this._intersectObs?.disconnect();
      this._productObs?.disconnect();
      this._drawerObs?.disconnect();

      this._intersectObs = null;
      this._productObs = null;
      this._drawerObs = null;

      clearTimeout(this._resizeTimer);
    }

    _isDesktop() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    _getOriginalBtn() {
      return document.getElementById(
        `ProductSubmitButton-${this._sectionId}`
      );
    }

    _getProductInfo() {
      return document.getElementById(`ProductInfo-${this._sectionId}`);
    }

    _getPriceText() {
      const productInfo = this._getProductInfo();
      if (!productInfo) return null;

      const priceContainer =
        document.getElementById(`price-${this._sectionId}`) || productInfo;

      const priceWrapper = priceContainer.matches?.('.price')
        ? priceContainer
        : priceContainer.querySelector('.price');

      if (!priceWrapper) return null;

      const visiblePrice = priceWrapper.classList.contains('price--on-sale')
        ? priceWrapper.querySelector('.price__sale .price-item--last')
        : priceWrapper.querySelector(
            '.price__regular .price-item--regular'
          );

      return visiblePrice?.textContent.trim() || null;
    }

    _getOriginalButtonText(button) {
      const textElement = Array.from(button.querySelectorAll('span')).find(
        (element) =>
          !element.closest('.loading__spinner') &&
          !element.classList.contains('visually-hidden')
      );

      if (textElement?.textContent.trim()) {
        return textElement.textContent.trim();
      }

      return Array.from(button.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join(' ')
        .trim();
    }

    _setVisible() {
      if (this._isDesktop() || this._isDrawerOpen) {
        this._setHidden(this._isDrawerOpen);
        return;
      }

      this.classList.remove('sticky-atc-bar--force-hidden');
      this.classList.add('sticky-atc-bar--visible');
      this.setAttribute('aria-hidden', 'false');
      this._stickyBtn.removeAttribute('tabindex');
    }

    _setHidden(force = false) {
      this.classList.remove('sticky-atc-bar--visible');
      this.classList.toggle('sticky-atc-bar--force-hidden', force);
      this.setAttribute('aria-hidden', 'true');
      this._stickyBtn.setAttribute('tabindex', '-1');
    }

    _updateVisibility() {
      if (this._isDrawerOpen) {
        this._setHidden(true);
        return;
      }

      if (this._isDesktop() || !this._pastOriginalButton) {
        this._setHidden(false);
        return;
      }

      this._setVisible();
    }

    _recalculateButtonPosition() {
      const button = this._getOriginalBtn();

      this._pastOriginalButton = Boolean(
        button && button.getBoundingClientRect().bottom < 0
      );
    }

    _mirrorState() {
      const originalButton = this._getOriginalBtn();
      const priceText = this._getPriceText();

      if (
        priceText &&
        this._priceEl &&
        this._priceEl.textContent.trim() !== priceText
      ) {
        this._priceEl.textContent = priceText;
      }

      if (!originalButton) return;

      const isLoading = originalButton.classList.contains('loading');

      const isDisabled =
        originalButton.disabled ||
        originalButton.getAttribute('aria-disabled') === 'true' ||
        isLoading;

      this._stickyBtn.disabled = isDisabled;
      this._stickyBtn.setAttribute(
        'aria-disabled',
        String(isDisabled)
      );
      this._stickyBtn.setAttribute('aria-busy', String(isLoading));

      const buttonText =
        this._getOriginalButtonText(originalButton);

      if (
        buttonText &&
        this._stickyBtn.textContent.trim() !== buttonText
      ) {
        this._stickyBtn.textContent = buttonText;

        if (this._liveEl) {
          this._liveEl.textContent = buttonText;
        }
      }
    }

    _attachIntersectionObserver() {
      const originalButton = this._getOriginalBtn();

      this._intersectObs?.disconnect();
      this._intersectObs = null;
      this._originalBtn = originalButton;

      if (!originalButton) {
        this._pastOriginalButton = false;
        this._updateVisibility();
        return;
      }

      this._recalculateButtonPosition();

      this._intersectObs = new IntersectionObserver(
        this._onIntersect,
        {
          threshold: 0,
        }
      );

      this._intersectObs.observe(originalButton);
      this._updateVisibility();
    }

    _onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;

      this._pastOriginalButton =
        !entry.isIntersecting &&
        entry.boundingClientRect.bottom < 0;

      this._updateVisibility();
    }

    _attachProductObserver() {
      const productInfo = this._getProductInfo();

      this._productObs?.disconnect();
      this._productObs = null;

      if (!productInfo) return;

      this._productObs = new MutationObserver(
        this._onProductMutate
      );

      this._productObs.observe(productInfo, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          'disabled',
          'aria-disabled',
          'class',
        ],
      });
    }

    _onProductMutate() {
      this._mirrorState();

      const currentButton = this._getOriginalBtn();

      if (currentButton !== this._originalBtn) {
        this._attachIntersectionObserver();
      }
    }

    _attachDrawerObserver() {
      const drawer = document.querySelector('cart-drawer');

      this._drawerObs?.disconnect();
      this._drawerObs = null;
      this._drawer = drawer;

      if (!drawer) return;

      this._drawerObs = new MutationObserver(
        this._onDrawerMutate
      );

      this._drawerObs.observe(drawer, {
        attributes: true,
        attributeFilter: [
          'class',
          'open',
          'aria-hidden',
        ],
      });
    }

    _updateDrawerState() {
      const drawer =
        this._drawer ||
        document.querySelector('cart-drawer');

      this._isDrawerOpen = Boolean(
        drawer &&
          (
            drawer.classList.contains('active') ||
            drawer.hasAttribute('open') ||
            drawer.getAttribute('aria-hidden') === 'false'
          )
      );
    }

    _onDrawerMutate() {
      const wasOpen = this._isDrawerOpen;

      this._updateDrawerState();

      if (wasOpen && !this._isDrawerOpen) {
        this._recalculateButtonPosition();
      }

      this._updateVisibility();
    }

    _onStickyClick() {
      const originalButton = this._getOriginalBtn();

      if (
        !originalButton ||
        originalButton.disabled ||
        originalButton.getAttribute('aria-disabled') === 'true' ||
        originalButton.classList.contains('loading')
      ) {
        return;
      }

      originalButton.click();
    }

    _onResize() {
      clearTimeout(this._resizeTimer);

      this._resizeTimer = setTimeout(() => {
        this._recalculateButtonPosition();
        this._updateVisibility();
      }, 150);
    }
  }

  customElements.define('sticky-atc-bar', StickyAtcBar);
}